import twinFields from '../twinFields';
import { mapState } from 'vuex';
const {
  evaluateTemplate,
  getDefinitions: getTwinFields
} = twinFields;
import get from 'lodash/get';
const hasValue = value => value !== null && value !== undefined && value !== '';
export default {
  name: 'TwinMapMarker',
  props: {
    twin: {
      type: Object,
      required: true
    },
    widget: {
      type: Object,
      required: true
    },
    customParameters: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      showPopup: false
    };
  },
  computed: {
    ...mapState('app', ['color']),
    attributes() {
      return get(this.widget, 'twinMapOptions.attributes', []);
    },
    actions() {
      return get(this.widget, 'twinMapOptions.actions', []);
    },
    twinFieldDefinitions() {
      return getTwinFields(this.$t, this.twin, {
        keyValueAtributes: this.attributes.filter(attribute => attribute.property.startsWith('twin.key_value__')).map(attribute => attribute.property.replace('twin.key_value__', ''))
      });
    },
    markerRules() {
      return get(this.widget, 'twinMapOptions.rules', []);
    },
    markerColor() {
      const defaultColor = this.color;
      if (this.markerRules.length === 0) {
        return defaultColor;
      }
      const firstApplicableRule = this.markerRules.find(rule => {
        const ruleField = get(rule, 'field', '');
        if (!ruleField) {
          return false;
        }
        const expectedValue = get(rule, 'value', '');
        const fieldDefinition = this.twinFieldDefinitions[ruleField];
        let actualValue = '';
        if (fieldDefinition) {
          actualValue = get(this.twin, fieldDefinition.key, '');
        }
        if (ruleField.startsWith('state.')) {
          actualValue = this.getLatestStateValue(ruleField.replace('state.', ''));
        }

        // Handle different comparison types properly
        switch (rule.operator) {
          case 'eq':
            // For equality, use loose comparison to handle string/number matches
            // eslint-disable-next-line eqeqeq
            return actualValue == expectedValue;
          case 'neq':
            // For inequality, use loose comparison to handle string/number matches
            // eslint-disable-next-line eqeqeq
            return actualValue != expectedValue;
          case 'gt':
          case 'gte':
          case 'lt':
          case 'lte':
            // For numeric comparisons, convert both values to numbers
            const actualNum = Number(actualValue);
            const expectedNum = Number(expectedValue);

            // If either value is NaN, fall back to string comparison
            if (isNaN(actualNum) || isNaN(expectedNum)) {
              const actualStr = String(actualValue);
              const expectedStr = String(expectedValue);
              switch (rule.operator) {
                case 'gt':
                  return actualStr > expectedStr;
                case 'gte':
                  return actualStr >= expectedStr;
                case 'lt':
                  return actualStr < expectedStr;
                case 'lte':
                  return actualStr <= expectedStr;
              }
            }

            // Use numeric comparison
            switch (rule.operator) {
              case 'gt':
                return actualNum > expectedNum;
              case 'gte':
                return actualNum >= expectedNum;
              case 'lt':
                return actualNum < expectedNum;
              case 'lte':
                return actualNum <= expectedNum;
            }
            break;
          default:
            return false;
        }
      });
      return firstApplicableRule ? firstApplicableRule.color : defaultColor;
    }
  },
  methods: {
    get,
    template(template) {
      return evaluateTemplate({
        twin: this.twin,
        customParameters: this.customParameters,
        template,
        i18n_t: this.$t
      });
    },
    getAttributeLabel(attribute) {
      if (attribute.label) {
        return attribute.label;
      }
      if (attribute.property.startsWith('state.')) {
        return attribute.property.replace('state.', '');
      }
      const definition = get(this.twinFieldDefinitions, `['${attribute.property}']`);
      if (!definition) {
        return attribute.property;
      }
      return definition.defaultLabel;
    },
    // shared by markerColor and getAttributeValue, so the marker color is evaluated against the value the popup shows
    getLatestStateValue(stateKey) {
      const histories = get(this.twin, `_states['${stateKey}'].history.histories`, []);
      const sortedHistories = [...histories].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      // Find the latest non-null, non-empty value instead of just the latest chronological value
      const latestHistoryItemWithValue = sortedHistories.find(item => hasValue(item.value));
      return get(latestHistoryItemWithValue, 'value', '');
    },
    getAttributeValue(attribute) {
      let value;
      if (attribute.property.startsWith('state.')) {
        value = this.getLatestStateValue(attribute.property.replace('state.', ''));
      } else {
        const definition = get(this.twinFieldDefinitions, `['${attribute.property}']`);
        if (!definition) {
          return attribute.property;
        }
        const propertyPath = definition.key;
        value = get(this.twin, propertyPath);
      }

      // a real 0 / false (e.g. a digital input that is off) must be shown, not replaced by the fallback
      return hasValue(value) ? value : this.$t('Dashboards.components.twinMap.popup.attributes.noValue');
    },
    onActionClick(action, twin) {
      this.$emit('click:action', {
        action,
        twin
      });
    }
  }
};